import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/acceptable-use")({
  head: () => ({
    meta: [
      { title: "Acceptable Use Policy — Lumvio" },
      {
        name: "description",
        content:
          "Rules for using Lumvio — prohibited content and conduct, and what will get an account suspended or banned.",
      },
      { property: "og:title", content: "Acceptable Use Policy — Lumvio" },
      { property: "og:description", content: "What is and is not allowed on the Lumvio platform." },
      { property: "og:url", content: "https://lumvio.co/acceptable-use" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/acceptable-use" }],
  }),
  component: AcceptableUsePage,
});

function AcceptableUsePage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout title={ar ? "سياسة الاستخدام المقبول" : "Acceptable Use Policy"} updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}>
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        This Acceptable Use Policy sets the rules for everyone who uses Lumvio. Violating it can result in warnings,
        content removal, account suspension, or permanent ban and forfeiture of funds subject to applicable law.
      </p>

      <h2>Prohibited content</h2>
      <ul>
        <li>Illegal, infringing, deceptive, or harmful content.</li>
        <li>Sexual content involving minors, human trafficking, or non-consensual material.</li>
        <li>Hate speech, harassment, or threats directed at any individual or group.</li>
        <li>Malware, phishing, or code intended to compromise systems or data.</li>
      </ul>

      <h2>Prohibited conduct</h2>
      <ul>
        <li>Circumventing payment protection or paying/receiving off-platform for work sourced through Lumvio.</li>
        <li>Providing false identity information or impersonating another person.</li>
        <li>Sharing your account or bypassing the 3-device account limit.</li>
        <li>Using Lumvio to launder funds, evade sanctions, or otherwise break the law.</li>
        <li>Scraping, reverse-engineering, or attacking the platform's infrastructure.</li>
      </ul>

      <h2>Reporting abuse</h2>
      <p>
        Report violations to <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>. Include screenshots, links, and
        any relevant context so we can investigate promptly.
      </p>

      <h2>Enforcement</h2>
      <p>
        Lumvio may remove content, freeze funds under review, suspend accounts, or permanently terminate access at
        our sole discretion when this policy is violated. Serious violations are reported to the appropriate
        authorities.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        تحدّد سياسة الاستخدام المقبول هذه القواعد لكل من يستخدم Lumvio. قد يؤدي انتهاكها إلى تحذيرات أو إزالة المحتوى
        أو تعليق الحساب أو الحظر الدائم ومصادرة الأموال وفقًا للقانون المعمول به.
      </p>

      <h2>المحتوى المحظور</h2>
      <ul>
        <li>محتوى غير قانوني أو منتهك للحقوق أو مضلل أو ضار.</li>
        <li>محتوى جنسي يتعلق بالقاصرين، أو الاتجار بالبشر، أو مواد بدون موافقة.</li>
        <li>خطاب كراهية أو مضايقة أو تهديدات موجَّهة إلى أي فرد أو جماعة.</li>
        <li>برامج ضارة أو تصيّد إلكتروني أو كود يهدف إلى اختراق الأنظمة أو البيانات.</li>
      </ul>

      <h2>السلوك المحظور</h2>
      <ul>
        <li>تجاوز نظام حماية المدفوعات أو الدفع/الاستلام خارج المنصة مقابل عمل مصدره Lumvio.</li>
        <li>تقديم معلومات هوية مزيّفة أو انتحال شخصية شخص آخر.</li>
        <li>مشاركة حسابك أو تجاوز حد الأجهزة الثلاثة للحساب.</li>
        <li>استخدام Lumvio لغسل الأموال أو التهرب من العقوبات أو خرق القانون بأي شكل آخر.</li>
        <li>استخراج البيانات (Scraping) أو الهندسة العكسية أو الاعتداء على البنية التحتية للمنصة.</li>
      </ul>

      <h2>الإبلاغ عن سوء الاستخدام</h2>
      <p>
        أبلِغ عن الانتهاكات إلى <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>. أرفق لقطات الشاشة والروابط وأي
        سياق ذي صلة حتى نتمكن من التحقيق بسرعة.
      </p>

      <h2>الإنفاذ</h2>
      <p>
        يجوز لـ Lumvio إزالة المحتوى، أو تجميد الأموال قيد المراجعة، أو تعليق الحسابات، أو إنهاء الوصول بشكل دائم وفق
        تقديرها المطلق عند انتهاك هذه السياسة. تُبلَّغ الانتهاكات الجسيمة إلى السلطات المختصة.
      </p>
    </>
  );
}
